import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  DESTINATIONS as DEFAULT_DESTINATIONS,
  SERVICES as DEFAULT_SERVICES,
  DEPARTMENTS as DEFAULT_DEPARTMENTS,
  AIR_ZIMBABWE as DEFAULT_AIRZIM,
} from "@/lib/data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContentContext = createContext({
  destinations: DEFAULT_DESTINATIONS,
  services: DEFAULT_SERVICES,
  departments: DEFAULT_DEPARTMENTS,
  airlinePartners: [],
  refresh: () => {},
});

const COLLECTIONS = ["destinations", "services", "departments", "airline_partners"];

function mergeWithDefaults(defaults, overrides, slugKey = "slug") {
  const map = new Map();
  defaults.forEach((d) => map.set(d[slugKey] || d.slug, d));
  overrides.forEach((o) => {
    const key = o.slug;
    if (map.has(key)) {
      map.set(key, { ...map.get(key), ...o });
    } else {
      map.set(key, o);
    }
  });
  return Array.from(map.values());
}

export function ContentProvider({ children }) {
  const [overrides, setOverrides] = useState({});

  const load = useCallback(async () => {
    try {
      const results = await Promise.all(
        COLLECTIONS.map((c) => axios.get(`${API}/content/${c}`).then((r) => [c, r.data.items]).catch(() => [c, []])),
      );
      const obj = {};
      results.forEach(([c, items]) => { obj[c] = items; });
      setOverrides(obj);
    } catch (error) {
      console.error("Failed to fetch content overrides:", error);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const destinations = mergeWithDefaults(DEFAULT_DESTINATIONS, overrides.destinations || []);
  const services = mergeWithDefaults(DEFAULT_SERVICES, overrides.services || []);
  const departments = mergeWithDefaults(DEFAULT_DEPARTMENTS, overrides.departments || [], "id");
  const airlinePartners = overrides.airline_partners || [];

  return (
    <ContentContext.Provider value={{ destinations, services, departments, airlinePartners, refresh: load }}>
      {children}
    </ContentContext.Provider>
  );
}

export const useContent = () => useContext(ContentContext);
